import { supabase } from '../lib/supabase';

export const mfaService = {
  // Check if user has MFA enabled
  async getMfaStatus(userId) {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('mfa_enabled, mfa_enrolled_at')
        ?.eq('id', userId)
        ?.single();

      if (error) throw error;

      return {
        data: {
          mfaEnabled: data?.mfa_enabled,
          mfaEnrolledAt: data?.mfa_enrolled_at
        },
        error: null
      };
    } catch (error) {
      console.error('Error fetching MFA status:', error);
      return { data: null, error };
    }
  },

  // Enroll in MFA (TOTP)
  async enrollMfa() {
    try {
      const { data, error } = await supabase?.auth?.mfa?.enroll({
        factorType: 'totp'
      });

      if (error) throw error;

      return {
        data: {
          id: data?.id,
          type: data?.type,
          qrCode: data?.totp?.qr_code,
          secret: data?.totp?.secret,
          uri: data?.totp?.uri
        },
        error: null
      };
    } catch (error) {
      console.error('Error enrolling MFA:', error);
      return { data: null, error };
    }
  },

  // Verify MFA enrollment with code from authenticator app
  async verifyEnrollment(factorId, code) {
    try {
      const challenge = await supabase?.auth?.mfa?.challenge({ factorId });
      if (challenge?.error) throw challenge?.error;

      const verify = await supabase?.auth?.mfa?.verify({
        factorId,
        challengeId: challenge?.data?.id,
        code
      });

      if (verify?.error) throw verify?.error;

      // Update user_profiles to mark MFA as enabled
      const { data: { user } } = await supabase?.auth?.getUser();
      if (user) {
        await supabase
          ?.from('user_profiles')
          ?.update({
            mfa_enabled: true,
            mfa_enrolled_at: new Date()?.toISOString()
          })
          ?.eq('id', user?.id);
      }

      return { data: verify?.data, error: null };
    } catch (error) {
      console.error('Error verifying MFA enrollment:', error);
      return { data: null, error };
    }
  },

  // Challenge MFA during login
  async challengeMfa(factorId) {
    try {
      const { data, error } = await supabase?.auth?.mfa?.challenge({ factorId });
      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error challenging MFA:', error);
      return { data: null, error };
    }
  },

  // Verify MFA code during login
  async verifyMfaCode(factorId, challengeId, code) {
    try {
      const { data, error } = await supabase?.auth?.mfa?.verify({
        factorId,
        challengeId,
        code
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error verifying MFA code:', error);
      return { data: null, error };
    }
  },

  // Get list of enrolled factors
  async listFactors() {
    try {
      const { data, error } = await supabase?.auth?.mfa?.listFactors();
      if (error) throw error;

      return { data: data?.totp || [], error: null };
    } catch (error) {
      console.error('Error listing MFA factors:', error);
      return { data: [], error };
    }
  },

  // Unenroll from MFA
  async unenrollMfa(factorId) {
    try {
      const { data, error } = await supabase?.auth?.mfa?.unenroll({ factorId });
      if (error) throw error;

      // Update user_profiles
      const { data: { user } } = await supabase?.auth?.getUser();
      if (user) {
        await supabase
          ?.from('user_profiles')
          ?.update({
            mfa_enabled: false,
            mfa_enrolled_at: null
          })
          ?.eq('id', user?.id);
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error unenrolling MFA:', error);
      return { data: null, error };
    }
  },

  // Generate backup codes
  async generateBackupCodes(userId) {
    try {
      const { data, error } = await supabase?.rpc('generate_mfa_backup_codes', {
        p_user_id: userId
      });

      if (error) throw error;

      return { data: data?.map(row => row?.code) || [], error: null };
    } catch (error) {
      console.error('Error generating backup codes:', error);
      return { data: [], error };
    }
  },

  // Verify backup code
  async verifyBackupCode(userId, code) {
    try {
      const { data, error } = await supabase?.rpc('verify_mfa_backup_code', {
        p_user_id: userId,
        p_code: code
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error verifying backup code:', error);
      return { data: false, error };
    }
  },

  // Get remaining backup codes count
  async getBackupCodesCount(userId) {
    try {
      const { count, error } = await supabase
        ?.from('mfa_backup_codes')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('user_id', userId)
        ?.eq('used', false);

      if (error) throw error;
      return { data: count || 0, error: null };
    } catch (error) {
      console.error('Error fetching backup codes count:', error);
      return { data: 0, error };
    }
  }
};